import { useEffect, useState } from 'react'

const STEPS = [
  { label: 'Install the SDK', detail: 'npm install one-email', done: false },
  { label: 'Send your first email', detail: '6 lines of code', done: false },
  { label: 'Verify your domain', detail: '2 DNS records', done: false },
] as const

export function EmailJourney() {
  const [completed, setCompleted] = useState(-1)

  useEffect(() => {
    let step = -1
    let timeout: ReturnType<typeof setTimeout>
    const tick = () => {
      step++
      if (step > STEPS.length) {
        return // stop — animation complete
      }
      setCompleted(step)
      timeout = setTimeout(tick, step === 0 ? 800 : 1200)
    }
    timeout = setTimeout(tick, 1000)
    return () => clearTimeout(timeout)
  }, [])

  const allDone = completed >= STEPS.length

  return (
    <div style={{ position: 'relative', zIndex: 2 }}>
      <div
        style={{
          background: '#fff',
          borderRadius: 20,
          border: '1px solid rgba(0,0,0,0.06)',
          padding: '32px 32px 28px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.02)',
          maxWidth: 400,
          marginLeft: 'auto',
        }}
      >
        {/* Header */}
        <p style={{
          fontSize: 13, fontWeight: 700, color: '#B5ADA9',
          letterSpacing: '0.04em', textTransform: 'uppercase' as const,
          marginBottom: 28,
        }}>
          Getting started
        </p>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {STEPS.map((step, i) => {
            const isCompleted = i < completed
            const isActive = i === completed
            const isPending = i > completed

            return (
              <div key={step.label}>
                {/* Step row */}
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                  padding: '16px 0',
                }}>
                  {/* Check circle */}
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 1,
                    background: isCompleted ? '#FF6B00' : isActive ? '#FFF5ED' : '#F7F3F0',
                    border: isActive ? '2px solid #FF6B00' : isCompleted ? '2px solid #FF6B00' : '2px solid #E8E0DB',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: isCompleted ? 'scale(1)' : isActive ? 'scale(1.05)' : 'scale(1)',
                  }}>
                    {isCompleted ? (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{
                        animation: 'oe-check-pop 0.3s ease-out',
                      }}>
                        <path
                          d="M2.5 7.5L5.5 10.5L11.5 3.5"
                          stroke="#fff"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <span style={{
                        fontSize: 13, fontWeight: 800,
                        fontFamily: 'var(--font-jetbrains), monospace',
                        color: isActive ? '#FF6B00' : '#C5BBB6',
                        transition: 'color 0.3s',
                      }}>
                        {i + 1}
                      </span>
                    )}
                  </div>

                  {/* Text */}
                  <div>
                    <p style={{
                      fontSize: 16, fontWeight: 700,
                      color: isPending ? '#C5BBB6' : '#1a1216',
                      transition: 'color 0.3s',
                      lineHeight: 1.3,
                      marginBottom: 3,
                    }}>
                      {step.label}
                    </p>
                    <p style={{
                      fontSize: 13,
                      fontFamily: 'var(--font-jetbrains), monospace',
                      color: isPending ? '#DDD6D1' : '#A89E9A',
                      transition: 'color 0.3s',
                    }}>
                      {step.detail}
                    </p>
                  </div>
                </div>

                {/* Connector line between steps */}
                {i < STEPS.length - 1 && (
                  <div style={{
                    width: 2, height: 12,
                    marginLeft: 15,
                    background: isCompleted ? '#FF6B00' : '#E8E0DB',
                    borderRadius: 1,
                    transition: 'background 0.4s',
                  }} />
                )}
              </div>
            )
          })}
        </div>

        {/* Bottom bar */}
        <div style={{
          marginTop: 24, paddingTop: 20,
          borderTop: '1px solid rgba(0,0,0,0.05)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: allDone ? 1 : 0.5,
            transition: 'opacity 0.5s',
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: allDone ? '#28C840' : '#E8E0DB',
              boxShadow: allDone ? '0 0 8px rgba(40,200,64,0.4)' : 'none',
              transition: 'all 0.5s',
            }} />
            <span style={{
              fontSize: 13, fontWeight: 600,
              color: allDone ? '#1a1216' : '#C5BBB6',
              transition: 'color 0.5s',
            }}>
              {allDone ? 'Ready to send' : 'Setting up...'}
            </span>
          </div>
          <span style={{
            fontSize: 12, color: '#B5ADA9',
            fontFamily: 'var(--font-jetbrains), monospace',
          }}>
            ~5 min
          </span>
        </div>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes oe-check-pop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.3); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  )
}
